// src/pages/admin/RoleManage.js
// 后台 · 管理中心 · 角色管理（doc01 3.3，缺陷 115/116）
// 结构：筛选（角色名称模糊）+ 查询·重置；表上方「新增」。
//  列：角色名称｜创建时间｜操作。
//  操作：编辑｜详情｜复制｜权限配置｜删除。
//  规则：管理员(内置)不可编辑/删除/复制；平台技术支持、现场运维可编辑菜单权限不可删除；自定义角色可复制/修改；
//       正在被成员账号使用的角色不可删除。
//  「权限配置」进入二级页面，按菜单树分配该角色可见的功能菜单。
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Input, Modal, Space, Tag, Tree, message } from 'antd';
import { SearchOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick, IA } from '../../config/menuConfig';
import { H_ROLES } from '../../config/stationData';

const labelStyle = { color: 'rgba(255,255,255,0.55)', fontSize: 12 };

// 菜单树（由云平台信息架构 IA 派生：分区 → 子分区 → 菜单页面）
const MENU_TREE = IA.map((g) => ({
  title: pick(g.label, 'zh'),
  key: g.key,
  children: (g.children || []).map((s) => ({
    title: pick(s.label, 'zh'),
    key: s.key,
    children: (s.children || []).map((l) => ({ title: pick(l.label, 'zh'), key: l.key })),
  })),
}));
const collectLeaves = () => {
  const out = [];
  IA.forEach((g) => (g.children || []).forEach((s) => (s.children || []).forEach((l) => out.push(l.key))));
  return out;
};
const ALL_MENUS = collectLeaves();

const PRESET_PERMS = {
  管理员: ALL_MENUS,
  平台技术支持: ['/app/monitor/station', '/app/monitor/realtime', '/app/alarm/fault', '/app/analysis/report'],
  现场运维: ['/app/monitor/station', '/app/om/workorder', '/app/om/plan'],
  运营专员: ['/app/display/multi', '/app/display/single', '/app/analysis/report', '/app/biz/response'],
  只读访客: ['/app/display/multi', '/app/display/single', '/app/display/map', '/app/analysis/report'],
};

const RoleManage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/admin/role');

  const [roles, setRoles] = useState(() => H_ROLES.map((r) => ({ ...r })));
  const [fName, setFName] = useState('');
  const [applied, setApplied] = useState('');

  const [roleModal, setRoleModal] = useState({ open: false, name: '', copyFrom: null });
  const [editModal, setEditModal] = useState({ open: false, key: null, name: '' });
  const [detailModal, setDetailModal] = useState({ open: false, role: '', menus: [] });
  const [permView, setPermView] = useState(null); // { key, name }
  const [permMenus, setPermMenus] = useState([]);
  const [permsMap, setPermsMap] = useState({});

  const permsOf = (r) => permsMap[r.key] || PRESET_PERMS[r.name] || [];
  const canEditName = (r) => r.name !== '管理员';
  const canCopy = (r) => r.name !== '管理员';
  const canPerm = (r) => r.name !== '管理员';
  const canDelete = (r) => !r.builtin;

  const rows = roles.filter((r) => !applied || r.name.includes(applied.trim()));
  const doQuery = () => { setApplied(fName); message.success('已按角色名称查询'); };
  const doReset = () => { setFName(''); setApplied(''); message.success('查询条件已重置'); };

  const openAdd = () => setRoleModal({ open: true, name: '', copyFrom: null });
  const copyRole = (r) => setRoleModal({ open: true, name: `${r.name} 副本`, copyFrom: r.key });
  const submitRole = () => {
    const name = roleModal.name.trim();
    if (!name) { message.warning('请输入角色名称'); return; }
    if (roles.some((r) => r.name === name)) { message.warning('角色名称不可与已有角色重复'); return; }
    const src = roleModal.copyFrom ? roles.find((r) => r.key === roleModal.copyFrom) : null;
    const key = `r-${Date.now()}`;
    const today = new Date().toISOString().slice(0, 10);
    setRoles((rs) => [...rs, {
      key, name, builtin: false, members: 0, created: today,
      desc: src ? `复制自「${src.name}」` : '自定义角色，权限为主管理员权限的子集',
    }]);
    if (src) setPermsMap((m) => ({ ...m, [key]: permsOf(src) }));
    setRoleModal({ open: false, name: '', copyFrom: null });
    message.success(src ? `已复制角色「${src.name}」为「${name}」` : `角色「${name}」已新增，请点击「权限配置」配置菜单权限`);
  };
  const submitEdit = () => {
    const name = editModal.name.trim();
    if (!name) { message.warning('请输入角色名称'); return; }
    if (roles.some((r) => r.name === name && r.key !== editModal.key)) { message.warning('角色名称不可与已有角色重复'); return; }
    setRoles((rs) => rs.map((r) => (r.key === editModal.key ? { ...r, name } : r)));
    setEditModal((s) => ({ ...s, open: false }));
    message.success('角色名称已修改');
  };
  const removeRole = (r) => {
    if (!canDelete(r)) { message.warning('内置角色不可删除'); return; }
    if (r.members > 0) { message.error('该角色正在被成员账号使用，无法删除'); return; }
    setRoles((rs) => rs.filter((x) => x.key !== r.key));
    message.success(`已删除角色「${r.name}」`);
  };
  const openDetail = (r) => setDetailModal({ open: true, role: r.name, menus: permsOf(r) });
  const openPerm = (r) => { setPermView({ key: r.key, name: r.name }); setPermMenus(permsOf(r)); };
  const savePerms = () => {
    setPermsMap((m) => ({ ...m, [permView.key]: permMenus }));
    message.success(`已保存「${permView.name}」的菜单权限（${permMenus.length} 项）`);
    setPermView(null);
  };

  const columns = [
    { title: '角色名称', dataIndex: 'name', render: (v, r) => (
      <span style={{ color: '#00d4ff' }}>{v}{r.builtin ? <Tag color="blue" style={{ marginLeft: 8 }}>内置</Tag> : null}</span>
    ) },
    { title: '创建时间', dataIndex: 'created', width: 160 },
    {
      title: '操作', key: 'op', width: 340,
      render: (_, r) => (
        <Space size={2} wrap>
          <Button type="link" size="small" disabled={!canEditName(r)} onClick={() => setEditModal({ open: true, key: r.key, name: r.name })}>编辑</Button>
          <Button type="link" size="small" onClick={() => openDetail(r)}>详情</Button>
          <Button type="link" size="small" disabled={!canCopy(r)} onClick={() => copyRole(r)}>复制</Button>
          <Button type="link" size="small" disabled={!canPerm(r)} onClick={() => openPerm(r)}>权限配置</Button>
          <Button type="link" size="small" danger disabled={!canDelete(r)} onClick={() => removeRole(r)}>删除</Button>
        </Space>
      ),
    },
  ];

  // 权限配置二级页面
  if (permView) {
    return (
      <PageSkeleton
        title={meta ? pick(meta.label, lang) : '角色管理'}
        desc={meta ? pick(meta.desc, lang) : ''}
        crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
        filters={[]}
        actions={[]}
      >
        <div className="panel">
          <div className="panel-title">
            权限配置 · {permView.name}
            <small>勾选该角色可访问的功能菜单（已选 {permMenus.length} / {ALL_MENUS.length}）</small>
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setPermView(null)}>返回</Button>
            <Button size="small" onClick={() => setPermMenus(ALL_MENUS)}>全选</Button>
            <Button size="small" onClick={() => setPermMenus([])}>清空</Button>
          </div>
          <Tree
            checkable
            selectable={false}
            defaultExpandAll
            treeData={MENU_TREE}
            checkedKeys={permMenus}
            onCheck={(keys) => {
              const arr = Array.isArray(keys) ? keys : keys.checked;
              setPermMenus(arr.filter((k) => ALL_MENUS.includes(k)));
            }}
          />
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={savePerms}>保存</Button>
          </div>
        </div>
      </PageSkeleton>
    );
  }

  return (
    <PageSkeleton
      title={meta ? pick(meta.label, lang) : '角色管理'}
      desc={meta ? pick(meta.desc, lang) : ''}
      crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
      filters={[]}
      actions={[]}
    >
      <div className="panel">
        <div className="panel-title">角色列表<small>共 {rows.length} 个角色</small></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 10 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={labelStyle}>角色名称</span>
            <Input size="small" value={fName} onChange={(e) => setFName(e.target.value)} placeholder="输入角色名称模糊查询" prefix={<SearchOutlined />} style={{ width: 220 }} allowClear />
          </span>
          <Space>
            <Button type="primary" size="small" onClick={doQuery}>查询</Button>
            <Button size="small" onClick={doReset}>重置</Button>
          </Space>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>新增</Button>
        </div>
        <Table rowKey="key" dataSource={rows} columns={columns} pagination={{ pageSize: 10, size: 'small' }} size="middle" scroll={{ x: 700 }} />
      </div>

      {/* 新增 / 复制角色 */}
      <Modal title={roleModal.copyFrom ? '复制角色' : '新增角色'} open={roleModal.open} onOk={submitRole}
        onCancel={() => setRoleModal({ open: false, name: '', copyFrom: null })} okText="确定" cancelText="取消" destroyOnHidden>
        <Input value={roleModal.name} onChange={(e) => setRoleModal((s) => ({ ...s, name: e.target.value }))} onPressEnter={submitRole}
          placeholder="请输入角色名称（不可与已有角色重复）" maxLength={20} />
        <div style={{ marginTop: 10, ...labelStyle }}>
          {roleModal.copyFrom ? '将原角色的菜单权限复制为新角色，需填写新的角色名称。' : '新增后需点击角色行的「权限配置」勾选功能菜单。'}
        </div>
      </Modal>

      {/* 编辑角色名称 */}
      <Modal title="编辑角色" open={editModal.open} onOk={submitEdit} onCancel={() => setEditModal((s) => ({ ...s, open: false }))} okText="确定" cancelText="取消" destroyOnHidden>
        <div style={{ marginBottom: 6, ...labelStyle }}>角色名称</div>
        <Input value={editModal.name} onChange={(e) => setEditModal((s) => ({ ...s, name: e.target.value }))} onPressEnter={submitEdit} placeholder="请输入角色名称" maxLength={20} />
      </Modal>

      {/* 角色详情 */}
      <Modal title={`角色详情 · ${detailModal.role}`} open={detailModal.open} onCancel={() => setDetailModal((s) => ({ ...s, open: false }))}
        footer={<Button type="primary" onClick={() => setDetailModal((s) => ({ ...s, open: false }))}>关闭</Button>} width={560} destroyOnHidden>
        <div style={{ marginBottom: 12, ...labelStyle }}>配置的菜单权限（{detailModal.menus.length} 项）</div>
        {detailModal.menus.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {detailModal.menus.map((k) => <Tag key={k} color="blue">{k}</Tag>)}
          </div>
        ) : <div style={{ color: 'rgba(255,255,255,0.4)' }}>暂未配置菜单权限，请点击「权限配置」勾选。</div>}
      </Modal>
    </PageSkeleton>
  );
};

export default RoleManage;
